import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { ExternalLink, Pencil } from 'lucide-react';
import { STAFF_TESTING_DEVICE_OPS_SCHEDULES } from '@staff-app/graphql-client';
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { safeJsonParse } from '@/lib/safe-format';
import {
  BUILTIN_OPS_IDS,
  formatSimpleSchedule,
  opsScheduleFeaturePatch,
  parseSimpleRRule,
  WEEK_DAYS,
  type SimpleSchedule,
} from './lib/ops-schedule';
import type { SettingsPayload } from './lib/settings-payload';
import { useDrawerScopeVars } from '../../lib/drawer-mode';

/** Where named Ops schedules are created / edited (scio3 route `/app/s/operational-schedule`). */
export const PORTAL_OPS_SCHEDULES_URL = 'https://app.optisigns.com/app/s/operational-schedule';

/** Select value that opens the inline days-and-hours editor instead of saving. */
const CUSTOM = '__custom__';

interface OpsScheduleRow {
  _id: string;
  name?: string | null;
}

interface Props {
  deviceId: string;
  feature: Record<string, unknown>;
  /** Persist a feature patch now; `rollback` runs when the save fails. Resolves true on success. */
  onSaveFeature: (patch: SettingsPayload, rollback: () => void) => Promise<boolean>;
}

/**
 * Operational-schedule picker — scio3 `DeviceOpsScheduleField` for staff: a single select of None ·
 * the built-in "Every day 04:00AM - 11:59PM" (or the device's own simple schedule) · the device
 * account+team's saved Ops schedules (read-only `staffTestingDeviceOpsSchedules`), applied on
 * select. Creating or editing a NAMED schedule stays in the customer portal — the ✏️ opens a note +
 * link there rather than scio3's editor dialog. "Custom days and hours…" is the one inline edit:
 * it writes the device's own simple RRULE/range (`scheduleOpsId: 'simple'`), like scio3's editor.
 */
export function OpsScheduleField({ deviceId, feature, onSaveFeature }: Props) {
  const scope = useDrawerScopeVars();
  const { data } = useQuery<{ staffTestingDeviceOpsSchedules: string | null }>(
    STAFF_TESTING_DEVICE_OPS_SCHEDULES,
    { variables: { deviceId, ...scope }, fetchPolicy: 'cache-and-network' },
  );
  const schedules = useMemo(
    () => safeJsonParse<OpsScheduleRow[]>(data?.staffTestingDeviceOpsSchedules, []) ?? [],
    [data?.staffTestingDeviceOpsSchedules],
  );

  const storedId = typeof feature.scheduleOpsId === 'string' ? feature.scheduleOpsId : '';
  const rrule = typeof feature.scheduleOperationalRRule === 'string' ? feature.scheduleOperationalRRule : '';
  const range = typeof feature.scheduleOperationalRange === 'string' ? feature.scheduleOperationalRange : '';
  const hasSimple = !!(rrule || range);
  const storedSimple = parseSimpleRRule(rrule, range);
  const builtinValue = hasSimple ? 'simple' : 'default';
  const builtinLabel = hasSimple
    ? formatSimpleSchedule(storedSimple)
    : 'Every day 04:00AM - 11:59PM';

  // Optimistic pick (reverted on a failed save) + the inline editor's draft.
  const [picked, setPicked] = useState<string | null>(null);
  const [editing, setEditing] = useState<SimpleSchedule | null>(null);
  const [busy, setBusy] = useState(false);

  const currentId = picked ?? storedId;
  const selectValue =
    currentId === '' || currentId === 'none'
      ? 'none'
      : currentId === 'default' || currentId === 'simple'
        ? builtinValue
        : currentId;
  const isNamed = !BUILTIN_OPS_IDS.has(currentId);
  const knownNamed = schedules.some((s) => s._id === currentId);

  const apply = async (id: string, simple?: SimpleSchedule) => {
    const prev = picked;
    setPicked(id);
    setBusy(true);
    // A saved/none/default id carries just scheduleOpsId; 'simple' re-sends the RRULE/range.
    const patch = opsScheduleFeaturePatch(id, id === 'simple' ? (simple ?? storedSimple) : undefined);
    const ok = await onSaveFeature(patch, () => setPicked(prev));
    setBusy(false);
    if (ok) setEditing(null);
  };

  const onSelect = (value: string) => {
    if (value === CUSTOM) {
      setEditing(storedSimple);
      return;
    }
    setEditing(null);
    if (value === selectValue) return;
    void apply(value);
  };

  const showEdit = isNamed || currentId === 'simple' || currentId === 'default';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select value={selectValue} onValueChange={onSelect} disabled={busy}>
          <SelectTrigger
            id="testing-set-ops"
            aria-label={'Operational Schedule'}
            className="min-w-0 flex-1 cursor-pointer"
            data-testid="testing-set-ops"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{'None'}</SelectItem>
            <SelectItem value={builtinValue}>{builtinLabel}</SelectItem>
            {schedules.map((s) => (
              <SelectItem key={s._id} value={s._id}>
                {s.name || s._id}
              </SelectItem>
            ))}
            {isNamed && currentId && !knownNamed ? (
              <SelectItem value={currentId}>{`Saved schedule (${currentId})`}</SelectItem>
            ) : null}
            <SelectSeparator />
            <SelectItem value={CUSTOM}>{'Custom days and hours…'}</SelectItem>
          </SelectContent>
        </Select>

        {showEdit ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="default"
                size="icon-md"
                className="shrink-0 cursor-pointer"
                aria-label={'Edit'}
                title={'Edit'}
                data-testid="testing-set-ops-edit"
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-2 text-sm" data-testid="testing-set-ops-edit-note">
              <p>{'Edit schedules in the customer portal.'}</p>
              <p className="text-xs text-muted-foreground">
                {
                  'Named Ops schedules are created and edited there; this console can only assign one. For simple days and hours, pick "Custom days and hours…".'
                }
              </p>
              <a
                href={PORTAL_OPS_SCHEDULES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                {'Open Operational Schedules'}
                <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>

      {editing ? (
        <div className="space-y-3 rounded-md border p-3" data-testid="testing-settings-ops-simple">
          <div className="flex flex-wrap gap-1.5">
            {WEEK_DAYS.map((d) => {
              const on = editing.days.includes(d.value);
              return (
                <Button
                  key={d.value}
                  type="button"
                  variant={on ? 'default' : 'outline'}
                  size="icon-sm"
                  className="shrink-0 rounded-full text-xs"
                  aria-label={d.name}
                  aria-pressed={on}
                  onClick={() => {
                    const days = on
                      ? editing.days.filter((x) => x !== d.value)
                      : [...editing.days, d.value].sort((a, b) => a - b);
                    if (days.length) setEditing({ ...editing, days });
                  }}
                  data-testid={`testing-set-ops-day-${d.byDay}`}
                >
                  {d.short}
                </Button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="testing-set-ops-from" className="text-sm font-normal">
                {'On from'}
              </Label>
              <Input
                id="testing-set-ops-from"
                type="time"
                value={editing.from}
                onChange={(e) => setEditing({ ...editing, from: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="testing-set-ops-to" className="text-sm font-normal">
                {'Until'}
              </Label>
              <Input
                id="testing-set-ops-to"
                type="time"
                value={editing.to}
                onChange={(e) => setEditing({ ...editing, to: e.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">{formatSimpleSchedule(editing)}</p>
            <div className="flex shrink-0 gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)} disabled={busy}>
                {'Cancel'}
              </Button>
              <Button
                size="sm"
                onClick={() => void apply('simple', editing)}
                disabled={busy}
                data-testid="testing-settings-ops-apply"
              >
                {'Apply'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
